import { useAuth } from "@clerk/clerk-react";
import { Box, Grid, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import lodash from "lodash";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DefaultVaOnboardingStepInfo,
  DefaultVaOnboardingSteps,
  VAStatus,
  VaOnboardingStep,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import StyledLargeButton from "components/Styled/Button/StyledLargeButton";
import { fetcherAuth } from "utils/api";

import styles from "./styles";

const OnboardingProgress = () => {
  const { getToken } = useAuth();
  const { setErrorAlert } = useContext(AlertContext);
  const [resumeLoading, setResumeLoading] = useState(false);
  const navigate = useNavigate();

  const { organizationVaProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );

  const isComplete = (index: number) => {
    if (isInMiddleOfDifferentOnboarding()) return false;
    return (
      index < (organizationVaProfile?.onboardingState?.currentStepIndex || 0)
    );
  };

  const calculatePercentageOfCompletion = () => {
    const currentStepIndex =
      organizationVaProfile?.onboardingState?.currentStepIndex || 0;
    const totalSteps = DefaultVaOnboardingSteps.length;
    return Math.round((currentStepIndex / totalSteps) * 100);
  };

  // Edge case: user in the middle of an old onboarding flow but never complete
  const isInMiddleOfDifferentOnboarding = () => {
    if (!organizationVaProfile?.onboardingState) return false;
    return !lodash.isEqual(
      organizationVaProfile.onboardingState.steps,
      DefaultVaOnboardingSteps,
    );
  };

  const handleProceed = async () => {
    setResumeLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/va-status`,
        "PATCH",
        {},
        {
          status: VAStatus.pending,
        },
      );
      navigate(`/${routes.onboardingVaSelfServe}`);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setResumeLoading(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F2F3FF",
        py: 3,
        px: { xs: 2, md: 6 },
        borderRadius: 4,
      }}
    >
      <Grid container gap={2} alignItems={"center"}>
        <Grid item xs md>
          {isInMiddleOfDifferentOnboarding() ? (
            <Typography
              gutterBottom
              sx={{ fontSize: { xs: 14, md: 18, fontWeight: 600 } }}
            >
              👋 We have updated our onboarding process since your last login
            </Typography>
          ) : (
            <Typography
              sx={{
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: { xs: 14, md: 18 },
              }}
              gutterBottom
            >
              You're {calculatePercentageOfCompletion()}% set up!
            </Typography>
          )}

          <Typography sx={{ fontSize: 16 }}>
            {isInMiddleOfDifferentOnboarding()
              ? "But don't worry, we still store your old information. Click Proceed Onboarding Setup to resume."
              : "Let's finish setup to start sending personalized brand pitches."}
          </Typography>
        </Grid>

        <Grid item xs={12} md="auto">
          <StyledLargeButton
            styles={{
              fontSize: { xs: 12, md: 16 },
              color: "purple.dark",
              border: `1px solid`,
              borderColor: "purple.dark",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "purple.light",
              },
            }}
            onClick={handleProceed}
            loading={resumeLoading}
          >
            Complete Onboarding Setup
          </StyledLargeButton>
        </Grid>
      </Grid>

      <Grid item xs={12} container gap={1} sx={{ mt: 3 }}>
        {DefaultVaOnboardingSteps?.map((step: VaOnboardingStep, index) =>
          step in DefaultVaOnboardingStepInfo ? (
            <Box
              sx={
                isComplete(index) ? styles.completeChip : styles.incompleteChip
              }
            >
              {/* technically there are the 'Welcome' screen which we track, but won't display on this UI here */}
              {isComplete(index) ? (
                <Box sx={styles.circle}>
                  <Box
                    component="i"
                    className="fa-regular fa-check"
                    sx={{ color: "white" }}
                  />
                </Box>
              ) : (
                <></>
              )}
              {DefaultVaOnboardingStepInfo[step]?.name}

              {isComplete(index) ? (
                ""
              ) : (
                <Typography sx={styles.timeText}>
                  ⏱️{" "}
                  {DefaultVaOnboardingStepInfo[step as VaOnboardingStep]?.time}{" "}
                  min
                </Typography>
              )}
            </Box>
          ) : null,
        )}
      </Grid>
    </Box>
  );
};

export default OnboardingProgress;
